import { useLocation, NavLink } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { Sun, Moon, Bell, Menu, Search } from "lucide-react";

const PAGE_TITLES = {
  "/dashboard": { title: "Overview", sub: "Your marketplace at a glance" },
  "/dashboard/products": { title: "Products", sub: "Manage your listings" },
  "/dashboard/incoming-orders": {
    title: "Incoming Orders",
    sub: "Telegram and manual orders in one queue",
  },
  "/dashboard/categories": {
    title: "Categories",
    sub: "Browse market categories",
  },
  "/dashboard/profile": {
    title: "Seller Profile",
    sub: "Your public seller page",
  },
  "/dashboard/notifications": {
    title: "Notifications",
    sub: "Stay up to date",
  },
  "/dashboard/pricing": { title: "Pricing", sub: "Plans & subscription" },
};

export default function Topbar({ onMenuClick }) {
  const { isDark, toggleTheme } = useTheme();
  const { user } = useAuth();
  const { unreadCount } = useApp();
  const { pathname } = useLocation();
  const notifs = unreadCount(user?.id);

  const page = PAGE_TITLES[pathname] || { title: "Dashboard", sub: "" };

  return (
    <header
      className={`h-16 backdrop-blur-md border-b flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30 flex-shrink-0 transition-colors duration-300 ${
        isDark
          ? "bg-gray-900/80 border-gray-800"
          : "bg-white/90 border-gray-200 shadow-sm"
      }`}>
      {/* Left: hamburger + page title */}
      <div className="flex items-center gap-4 min-w-0">
        <button
          onClick={onMenuClick}
          className={`lg:hidden p-2 rounded-xl transition-colors ${
            isDark
              ? "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
              : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          }`}>
          <Menu size={20} />
        </button>
        <div className="min-w-0">
          <h1
            className={`text-base font-semibold truncate ${
              isDark ? "text-gray-100" : "text-gray-900"
            }`}>
            {page.title}
          </h1>
          <p
            className={`text-xs truncate hidden sm:block ${
              isDark ? "text-gray-500" : "text-gray-400"
            }`}>
            {page.sub}
          </p>
        </div>
      </div>

      {/* Right: search + actions */}
      <div className="flex items-center gap-2">
        {/* Search bar – visible on medium+ */}
        <div
          className={`hidden md:flex items-center gap-2 border rounded-xl px-3 py-2 w-48 xl:w-64 ${
            isDark
              ? "bg-gray-800 border-gray-700"
              : "bg-gray-100 border-gray-200"
          }`}>
          <Search
            size={14}
            className={`flex-shrink-0 ${isDark ? "text-gray-500" : "text-gray-400"}`}
          />
          <input
            type="text"
            placeholder="Search..."
            className={`bg-transparent text-sm focus:outline-none w-full ${
              isDark
                ? "text-gray-300 placeholder-gray-600"
                : "text-gray-700 placeholder-gray-400"
            }`}
          />
        </div>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
          className={`p-2.5 rounded-xl transition-colors ${
            isDark
              ? "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
              : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          }`}>
          {isDark ? (
            <Sun size={18} className="text-amber-400" />
          ) : (
            <Moon size={18} className="text-indigo-400" />
          )}
        </button>

        {/* Notifications */}
        <NavLink
          to="/dashboard/notifications"
          className={`relative p-2.5 rounded-xl transition-colors ${
            isDark
              ? "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
              : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          }`}>
          <Bell size={18} />
          {notifs > 0 && (
            <span
              className={`absolute top-1.5 right-1.5 w-2 h-2 bg-violet-500 rounded-full ring-2 ${
                isDark ? "ring-gray-900" : "ring-white"
              }`}
            />
          )}
        </NavLink>

        {/* Avatar */}
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0 cursor-default">
          {user?.initials}
        </div>
      </div>
    </header>
  );
}
